// Page: CreateEnterprise
// Purpose: Admin form to create an enterprise account; validates domain, builds
// FormData (logo optional), and posts to backend. Requires admin access.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner, ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { PageHeader } from '../../../components/ui/layout/page-header';
import { Card, CardContent } from '../../../components/ui/layout/card';
import { Label } from '../../../components/ui/inputs/label';
import { Textarea } from '../../../components/ui/inputs/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/inputs/select';
import { ErrorMessage } from '../../../components/ui/feedback/error-message.jsx';
import { Building2, ArrowLeft, Plus } from 'lucide-react';
import ImageUploader from '../../../components/admin/createTemplates/ImageUploader';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import api from '../../../api';
import { Input } from '../../../components/ui/inputs/input';

export default function CreateEnterprise() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    enterpriseName: '',
    enterpriseDomain: '',
    adminEmail: '',
    maxAccounts: ''
    // Removed planType, planBilling, and access control fields since they're always premium with full access
  });

  // Check if user is admin
  const isAdmin = isUserAdmin(user);

  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      } else {
        setIsLoading(false);
      }
    } else if (user === null) {
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) {
      setError(null);
    }
  };

  const handleImageChange = (file) => {
    setLogoFile(file);
    if (error) {
      setError(null);
    }
  };

  const validateForm = () => {
    const { enterpriseName, enterpriseDomain } = formData;

    if (!enterpriseName.trim()) {
      return 'Enterprise name is required';
    }

    if (!enterpriseDomain.trim()) {
      return 'Enterprise domain is required';
    }

    // Validate domain format
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(enterpriseDomain.trim())) {
      return 'Please enter a valid domain (e.g., company.com)';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Debug log the data before creating FormData
      console.log('Form data before submission:', {
        enterpriseName: formData.enterpriseName,
        enterpriseDomain: formData.enterpriseDomain,
        planType: formData.planType,
        planBilling: formData.planBilling,
        logo: logoFile
      });

      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add all required fields with validation
      submitData.append('enterpriseName', formData.enterpriseName.trim());
      submitData.append('enterpriseDomain', formData.enterpriseDomain.toLowerCase().trim());
      
      // Add admin email if provided
      if (formData.adminEmail && formData.adminEmail.trim()) {
        submitData.append('adminEmail', formData.adminEmail.toLowerCase().trim());
      }
      
      // Add maxAccounts if provided
      if (formData.maxAccounts && formData.maxAccounts.trim()) {
        const maxAccountsNum = parseInt(formData.maxAccounts.trim());
        if (!isNaN(maxAccountsNum) && maxAccountsNum > 0) {
          submitData.append('maxAccounts', maxAccountsNum.toString());
        }
      }
      
      // Always set to premium with full access
      submitData.append('planType', 'premium');
      submitData.append('planBilling', 'monthly');
      // Removed access control fields since they're always true on backend

      // Add logo file if provided
      if (logoFile && logoFile instanceof File) {
        submitData.append('logo', logoFile);
      }

      // Debug: Log FormData contents
      console.log('Submitting FormData with:');
      for (const pair of submitData.entries()) {
        console.log(`${pair[0]}:`, pair[1]);
      }
      
      // Validate required fields are present
      const requiredFields = ['enterpriseName', 'enterpriseDomain'];
      for (const field of requiredFields) {
        if (!submitData.get(field)) {
          throw new Error(`Missing required field: ${field}`);
        }
      }

      const response = await api.post('/admin/enterprises', submitData);
      
      addToast('Enterprise created successfully', 'success');
      
      // Navigate to manage enterprises page
      navigate('/app/private/manageEnterprises');
      
    } catch (err) {
      console.error('Error creating enterprise:', err);
      const errorMessage = err.response?.data?.error || 'Failed to create enterprise';
      setError(errorMessage);
      addToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Admin
        </button>
      </div>

      <PageHeader
        title="Create Enterprise"
        description="Create a new enterprise account with custom plan configuration"
      >
        <button
          onClick={() => navigate('/app/private/manageEnterprises')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
        >
          <Building2 className="h-4 w-4" />
          Manage Enterprises
        </button>
      </PageHeader>

      <Card className="mt-8">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="enterpriseName">Enterprise Name *</Label>
                <input
                  id="enterpriseName"
                  type="text"
                  value={formData.enterpriseName}
                  onChange={(e) => handleInputChange('enterpriseName', e.target.value)}
                  placeholder="e.g., Advocacy Lab Inc."
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <Label htmlFor="enterpriseDomain">Enterprise Domain *</Label>
                <Input
                  id="enterpriseDomain"
                  type="text"
                  value={formData.enterpriseDomain}
                  onChange={(e) => handleInputChange('enterpriseDomain', e.target.value)}
                  placeholder="company.com"
                  className="mt-1"
                  required
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Any user with an email from this domain can sign up for the enterprise plan after email verification.
                </p>
              </div>

              <div>
                <Label htmlFor="adminEmail">Admin Email</Label>
                <Input
                  id="adminEmail"
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                  placeholder="admin@company.com"
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Contact email for the enterprise administrator (for billing and support).
                </p>
              </div>

              <div>
                <Label htmlFor="maxAccounts">Max Accounts</Label>
                <input
                  id="maxAccounts"
                  type="text"
                  value={formData.maxAccounts}
                  onChange={(e) => handleInputChange('maxAccounts', e.target.value)}
                  placeholder="e.g., 100"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Maximum number of accounts allowed for this enterprise. Leave empty for unlimited accounts.
                </p>
              </div>

              {/* Removed Plan Type and Plan Billing fields */}

            </div>

            <div>
              <Label htmlFor="enterpriseLogo">Enterprise Logo</Label>
              <div className="mt-1">
                <ImageUploader
                  onImageChange={handleImageChange}
                  error={error && error.includes('logo') ? error : null}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Upload a company logo image (optional)
              </p>
            </div>

            {/* Enterprise Access Controls section removed - all enterprises get premium access */}

            {error && (
              <ErrorMessage message={error} />
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
              >
                {isSubmitting ? (
                  <>
                    <ButtonSpinner />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Create Enterprise
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/app/private/admin')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Footer />
    </div>
  );
} 